import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { Loan } from '@shared/definitions/loan';
import { projectLoansForProperty } from '@shared/utils/loan-allocation';
import { LoansService } from '@core/services/data/loans.service';

/**
 * Phase 4 — signal-based store for the loan list + currently-viewed
 * loan. Mirrors PropertyStore / ContractStore / TenantStore / etc.
 *
 * `LoansService` (at `src/app/services/financial/loans/loans.service.ts`)
 * owns the Firestore subscription; it mirrors its `loans` BehaviorSubject
 * into this store via a bridge. Consumers read signals here instead of
 * subscribing to the service observable.
 *
 * Write actions (addLoan/updateLoan/deleteLoan/fetchAll) delegate to
 * LoansService via a lazy Injector lookup. The service already injects
 * LoanStore (for the read-path bridge), so direct `inject(LoansService)`
 * here would create a circular DI at class-init time. Resolving the
 * service lazily inside the write methods sidesteps that — by the time
 * any consumer calls a write, both instances are fully constructed.
 */
@Injectable({ providedIn: 'root' })
export class LoanStore {
  private readonly _list = signal<readonly Loan[]>([]);
  private readonly _currentId = signal<string | null>(null);
  private readonly _loading = signal(false);

  private readonly injector = inject(Injector);
  private _service: LoansService | null = null;
  private get service(): LoansService {
    return (this._service ??= this.injector.get(LoansService));
  }

  readonly list = this._list.asReadonly();
  readonly currentId = this._currentId.asReadonly();
  readonly loading = this._loading.asReadonly();

  readonly count = computed(() => this._list().length);

  readonly current = computed(() => {
    const id = this._currentId();
    if (!id) return null;
    return this._list().find(l => l.loanUID === id) ?? null;
  });

  /**
   * Loans for a property: its direct loans PLUS a projected pro-rata view of
   * any building loan that allocates to it (see `projectLoansForProperty`).
   * Purely additive — a property with no building-loan allocation gets exactly
   * its direct loans, so existing behaviour is unchanged.
   */
  readonly byPropertyId = (propertyUID: string) =>
    computed(() => projectLoansForProperty(this._list(), propertyUID));

  /** Synchronous lookup. */
  getById(loanUID: string): Loan | null {
    return this._list().find(l => l.loanUID === loanUID) ?? null;
  }

  /** All loans for a property — synchronous snapshot (incl. building-loan shares). */
  getForProperty(propertyUID: string): readonly Loan[] {
    return projectLoansForProperty(this._list(), propertyUID);
  }

  setList(loans: readonly Loan[]): void {
    this._list.set(loans);
  }

  upsert(loan: Loan): void {
    const list = this._list();
    const i = list.findIndex(l => l.loanUID === loan.loanUID);
    if (i === -1) {
      this._list.set([...list, loan]);
    } else {
      const next = list.slice();
      next[i] = loan;
      this._list.set(next);
    }
  }

  remove(loanId: string): void {
    this._list.set(this._list().filter(l => l.loanUID !== loanId));
    if (this._currentId() === loanId) {
      this._currentId.set(null);
    }
  }

  setCurrentId(id: string | null): void {
    this._currentId.set(id);
  }

  setLoading(value: boolean): void {
    this._loading.set(value);
  }

  reset(): void {
    this._list.set([]);
    this._currentId.set(null);
    this._loading.set(false);
  }

  /** Create a loan. Returns the new loanUID on completion. */
  addLoan(loan: Omit<Loan, 'loanUID'>): Observable<string> {
    return this.service.addLoan(loan);
  }

  /** Patch an existing loan. */
  updateLoan(loanUID: string, patch: Partial<Loan>): Observable<void> {
    return this.service.updateLoan(loanUID, patch);
  }

  /** Delete a loan by id. */
  deleteLoan(loanUID: string): Observable<void> {
    return this.service.deleteLoan(loanUID);
  }

  /** Re-subscribe to the user-scoped loans collection. */
  fetchAll(): void {
    this.service.fetchAllLoans();
  }
}
